import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Typography,
} from "@mui/material";
import BDatePicker from "../../common/input/BDatePicker";
import moment from "moment";
import NumericTextField from "../../common/component/numeric/NumericTextField";
import MultipleSelect from "../../common/component/MultiSelect";

const DAYS_OF_WEEK = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function RulesSection({ title, description, note, children, divider = true }) {
  return (
    <Box>
      {divider && <Divider />}
      <Box py={2}>
        <Box mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="textSecondary">
              {description}
            </Typography>
          )}
          {note && (
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
              {note}
            </Typography>
          )}
        </Box>
        {children}
      </Box>
    </Box>
  );
}

/** Lead time, date range, and days of week — general item availability. */
export default function ProductRules(props) {
  const { value } = props;

  const [leadTime, setLeadTime] = useState(value?.date?.leadTime || "");
  const [minDate, setMinDate] = useState(value?.date?.min || "");
  const [maxDate, setMaxDate] = useState(value?.date?.max || "");
  const [daysToExclude, setDaysToExclude] = useState(value?.date?.daysToExclude || []);

  useEffect(() => {
    let rules = { ...value, date: { ...value?.date } };
    rules.date.leadTime = leadTime;
    rules.date.min = minDate;
    rules.date.max = maxDate;
    rules.date.daysToExclude = daysToExclude;

    props.onChange(rules);
  }, [leadTime, minDate, maxDate, daysToExclude]);

  return (
    <Box>
      <RulesSection
        title="Lead Time"
        description="Ensures you have enough time to prepare this item"
        note="Note: If the lead time extends past your business hours, then the lead time will automatically extend to your next business day."
        divider={false}
      >
        <FormControl fullWidth>
          <NumericTextField
            allowNegative={false}
            decimalScale={0}
            isAllowed={(values) => {
              const { value } = values;
              return value < 300;
            }}
            value={leadTime}
            onChange={(value) => {
              setLeadTime(value);
            }}
            label="Lead Time (Hours)"
            variant="outlined"
          />
        </FormControl>
        <FormHelperText>Max Hours: 299</FormHelperText>
      </RulesSection>

      <RulesSection
        title="Limit Availability by Fulfillment Date"
        description="Accept future orders only for dates that fall within a specified date range. Works great for seasonal items."
      >
        <Grid container spacing={2}>
          <Grid item sm={6} xs={12}>
            <BDatePicker
              value={value?.date?.min}
              label="Available From"
              onChange={(d) => setMinDate(d)}
              disablePast
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <BDatePicker
              value={value?.date?.max}
              label="Available To"
              onChange={(d) => setMaxDate(d)}
              disablePast
              minDate={moment(minDate).toDate()}
            />
          </Grid>
        </Grid>
      </RulesSection>

      <RulesSection
        title="Limit Availability by Days of Week"
        description="By default an item is available every day of the week. You can restrict availability to specific days below."
      >
        <FormControl variant="outlined" fullWidth required>
          <InputLabel id="daysToExclude-label">Days of Week</InputLabel>
          <MultipleSelect
            label="Days to Restrict"
            value={DAYS_OF_WEEK.filter((item) => !daysToExclude.join(",").includes(item))}
            options={DAYS_OF_WEEK}
            onChange={(daysAvailable) => {
              setDaysToExclude(
                DAYS_OF_WEEK.filter(
                  (item) => !daysAvailable.join(",").toLowerCase().includes(item.toLowerCase())
                )
              );
            }}
          />
        </FormControl>
      </RulesSection>
    </Box>
  );
}
